import {
	ReactNode,
	SyntheticEvent,
	useCallback,
	useEffect,
	useMemo,
	useState,useContext
} from "react";
import { useSearchParams } from "react-router-dom";

import TasksActive from "./ActiveTasks";
import CompletedTasks from "./CompletedTasks";
import RecurringTasks from "./RecurringTasks";
import { TaskPageContextProvider } from "@/features/task/hooks";
import { TaskGroup } from "@/features/task/model/Task/TaskRequest";
import {
	TaskDashboard,
	TaskGlobalSearchDialog,
	TaskHeaderActionsContextProvider,
	TaskHeaderPrimaryAction,
	TaskHeaderPrimaryActionContextProvider,
	TaskPageHeader,
} from "@/features/task/ui/components";
import { TabPanel } from "@/shared/ui/TabPanel";
import { StorageKeys } from "@/storage/storageKeys";
import { useSelectedTaskGroups } from "@/storage/useSelectedTaskGroups";
import { mobilePillTabsSx, sharedTabsSx } from "@/styles/sharedNavStyles";
import CloseIcon from "@mui/icons-material/Close";
import { useMediaQuery, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";

import { useFetchTaskGroups } from "@/service/TaskQueries";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";



interface TabPanelProps {
	children?: ReactNode;
	index: number;
	value: number;
	fill?: boolean;
}

function CustomTabPanel({
	children,
	index,
	value,
	fill,
	...other
}: TabPanelProps) {
	return (
		<TabPanel
			currentTab={value}
			index={index}
			fill={fill}
			panelId={`tasks-tabpanel-${index}`}
			labelledById={`tasks-tab-${index}`}
			{...other}
		>
			{children}
		</TabPanel>
	);
}

function a11yProps(index: number) {
	return {
		id: `tasks-tab-${index}`,
		"aria-controls": `tasks-tabpanel-${index}`,
	};
}

const TAB_SLUGS: Record<number, string> = {
	1: "active",
	2: "completed",
	3: "recurring",
};

const TAB_FROM_SLUG: Record<string, number> = Object.fromEntries(
	Object.entries(TAB_SLUGS).map(([key, value]) => [value, Number(key)]),
);

interface TaskGroupsResponse {
	Groups: TaskGroup[];
}

type TaskGroupsPayload = TaskGroupsResponse | TaskGroup[];

const isTaskGroupsResponse = (
	payload: TaskGroupsPayload | undefined,
): payload is TaskGroupsResponse =>
	Boolean(
		payload &&
		typeof payload === "object" &&
		!Array.isArray(payload) &&
		Array.isArray(payload.Groups),
	);

const normalizeGroupIds = (groupIds: number[]): number[] => {
	const uniqueIds = new Set<number>();
	for (const value of groupIds) {
		const parsed = Number(value);
		if (!Number.isInteger(parsed) || parsed <= 0) continue;
		uniqueIds.add(parsed);
	}
	return [...uniqueIds];
};

export const TasksHome = () => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const isMobile = useMediaQuery("(max-width:767px)");
	const [searchParams, setSearchParams] = useSearchParams();
	const [selectedGroupIds, setSelectedGroupIds] = useSelectedTaskGroups();
	const tabFromUrl = searchParams.get("tab");
	const valueFromUrl =
		tabFromUrl != null && TAB_FROM_SLUG[tabFromUrl] != null
			? TAB_FROM_SLUG[tabFromUrl]
			: 1;

	const [value, setValue] = useState(valueFromUrl);
	const [searchInput, setSearchInput] = useState("");
	const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
	const [overviewOpen, setOverviewOpen] = useState(false);
	const [tabActions, setTabActions] = useState<ReactNode>(null);
	const [tabPrimaryAction, setTabPrimaryAction] =
		useState<TaskHeaderPrimaryAction | null>(null);
	const [completedDateFrom, setCompletedDateFrom] = useState("");
	const [completedDateTo, setCompletedDateTo] = useState("");
	const [completedOnlyHandover, setCompletedOnlyHandover] = useState(false);
	const [completedOnlyShiftlog, setCompletedOnlyShiftlog] = useState(false);
	const [shouldSeedSelection, setShouldSeedSelection] = useState(() => {
		if (typeof window === "undefined") return true;
		return window.localStorage.getItem(StorageKeys.selectedGroupIds) == null;
	});
	const taskGroupsQuery = useFetchTaskGroups() as unknown as {
		data?: TaskGroupsPayload;
		isLoading: boolean;
	};
	const taskGroups = taskGroupsQuery.data;
	const normalizedTaskGroups = useMemo(() => {
		if (Array.isArray(taskGroups)) {
			return taskGroups;
		}
		if (isTaskGroupsResponse(taskGroups)) {
			return taskGroups.Groups;
		}
		return [];
	}, [taskGroups]);
	const normalizedSelectedGroupIds = useMemo(
		() => normalizeGroupIds(selectedGroupIds),
		[selectedGroupIds],
	);
	const availableGroupIdSet = useMemo(
		() => new Set(normalizedTaskGroups.map((group) => group.Id)),
		[normalizedTaskGroups],
	);
	const effectiveSelectedGroupIds = useMemo(
		() =>
			normalizedSelectedGroupIds.filter((groupId) =>
				availableGroupIdSet.has(groupId),
			),
		[normalizedSelectedGroupIds, availableGroupIdSet],
	);
	const isLoading = taskGroupsQuery.isLoading;

	useEffect(() => {
		setValue(valueFromUrl);
	}, [valueFromUrl]);

	useEffect(() => {
		if (normalizedTaskGroups.length === 0) {
			return;
		}

		const hasSelectionDiff =
			effectiveSelectedGroupIds.length !== selectedGroupIds.length ||
			effectiveSelectedGroupIds.some(
				(groupId, index) => groupId !== selectedGroupIds[index],
			);

		if (hasSelectionDiff) {
			setSelectedGroupIds(effectiveSelectedGroupIds);
			return;
		}

		if (shouldSeedSelection && effectiveSelectedGroupIds.length === 0) {
			setSelectedGroupIds(normalizedTaskGroups.map((group) => group.Id));
			setShouldSeedSelection(false);
			return;
		}

		if (shouldSeedSelection) {
			setShouldSeedSelection(false);
		}
	}, [
		normalizedTaskGroups,
		effectiveSelectedGroupIds,
		selectedGroupIds,
		setSelectedGroupIds,
		shouldSeedSelection,
	]);

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
				event.preventDefault();
				setGlobalSearchOpen(true);
			}
			if (event.key === "Escape") {
				setGlobalSearchOpen(false);
			}
		};

		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, []);

	const tabs = useMemo(
		() => [
			{ name: "Active", index: 1 },
			{ name: "Completed", index: 2 },
			{ name: "Recurring", index: 3 },
		],
		[],
	);

	const openTab = (
		tab: "active" | "completed" | "recurring",
		taskId?: number,
	) => {
		const nextValue = TAB_FROM_SLUG[tab];
		setValue(nextValue);
		const nextParams = new URLSearchParams();
		nextParams.set("tab", tab);
		if (taskId != null) {
			nextParams.set("taskId", String(taskId));
		}
		setSearchParams(nextParams, { replace: true });
	};

	const handleChange = (_event: SyntheticEvent, newValue: number) => {
		setValue(newValue);
		const slug = TAB_SLUGS[newValue];
		if (slug) {
			setSearchParams({ tab: slug }, { replace: true });
		}
	};

	const handleCloseOverview = useCallback(() => {
		setOverviewOpen(false);
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.delete("period");
				return next;
			},
			{ replace: true },
		);
	}, [setSearchParams]);

	const openTabFromOverview = useCallback(
		(tab: "active" | "completed" | "recurring", taskId?: number) => {
			handleCloseOverview();
			openTab(tab, taskId);
		},
		[handleCloseOverview],
	);

	const activeTabSlug = TAB_SLUGS[value] as
		| "active"
		| "completed"
		| "recurring";
	const taskPageContextValue = useMemo(
		() => ({
			searchInput,
			setSearchInput,
			selectedGroupIds: effectiveSelectedGroupIds,
			setSelectedGroupIds,
			completedDateFrom,
			setCompletedDateFrom,
			completedDateTo,
			setCompletedDateTo,
			completedOnlyHandover,
			setCompletedOnlyHandover,
			completedOnlyShiftlog,
			setCompletedOnlyShiftlog,
		}),
		[
			searchInput,
			effectiveSelectedGroupIds,
			completedDateFrom,
			completedDateTo,
			completedOnlyHandover,
			completedOnlyShiftlog,
		],
	);

	return (
		<TaskPageContextProvider value={taskPageContextValue}>
			<TaskHeaderActionsContextProvider value={setTabActions}>
				<TaskHeaderPrimaryActionContextProvider value={setTabPrimaryAction}>
					<Box
						sx={{
							width: "100%",
							flex: 1,
							minHeight: 0,
							display: "flex",
							flexDirection: "column",
							bgcolor: "background.default",
						}}
					>
						<TaskPageHeader
							searchInput={searchInput}
							onSearchChange={setSearchInput}
							selectedGroupIds={effectiveSelectedGroupIds}
							onGroupChange={setSelectedGroupIds}
							taskGroups={normalizedTaskGroups}
							loading={isLoading}
							onOpenGlobalSearch={() => setGlobalSearchOpen(true)}
							onOverviewClick={() => setOverviewOpen(true)}
							activeTab={activeTabSlug}
							completedDateFrom={completedDateFrom}
							onCompletedDateFromChange={setCompletedDateFrom}
							completedDateTo={completedDateTo}
							onCompletedDateToChange={setCompletedDateTo}
							completedOnlyHandover={completedOnlyHandover}
							onCompletedOnlyHandoverChange={setCompletedOnlyHandover}
							completedOnlyShiftlog={completedOnlyShiftlog}
							onCompletedOnlyShiftlogChange={setCompletedOnlyShiftlog}
							extraActions={tabActions}
							primaryAction={tabPrimaryAction}
							tabs={
								<Tabs
									data-tour="tasks-tabs"
									variant="scrollable"
									scrollButtons="auto"
									allowScrollButtonsMobile
									value={value}
									onChange={handleChange}
									aria-label={GetLanguageValueAsString(translation, "Auto.Features.Task.Pages.TasksHome.L345C21.Tasks", "Tasks")}
									textColor="inherit"
									indicatorColor="primary"
									sx={isMobile ? mobilePillTabsSx(theme) : sharedTabsSx}
								>
									{tabs.map((tab) => (
										<Tab
											key={tab.index}
											value={tab.index}
											label={tab.name}
											{...a11yProps(tab.index)}
										/>
									))}
								</Tabs>
							}
						/>

						<Box
							sx={{
								flex: 1,
								minHeight: 0,
								overflow: "hidden",
								display: "flex",
								flexDirection: "column",
							}}
						>
							<CustomTabPanel value={value} index={1} fill>
								<TasksActive />
							</CustomTabPanel>
							<CustomTabPanel value={value} index={2} fill>
								<CompletedTasks />
							</CustomTabPanel>
							<CustomTabPanel value={value} index={3} fill>
								<RecurringTasks />
							</CustomTabPanel>
						</Box>

						<TaskGlobalSearchDialog
							open={globalSearchOpen}
							onClose={() => setGlobalSearchOpen(false)}
							selectedGroupIds={effectiveSelectedGroupIds}
							onOpenTab={openTab}
						/>
						<Dialog
							open={overviewOpen}
							onClose={handleCloseOverview}
							maxWidth="xl"
							fullWidth
						>
							<DialogTitle
								sx={{
									position: "relative",
									pr: 6,
									minWidth: 0,
								}}
							>
								<Typography variant="h6" fontWeight={800} noWrap>
									<Translate String="Auto.Features.Task.Pages.TasksHome.L399C58.TaskDashboard">Task dashboard</Translate>
								</Typography>
								<IconButton
									onClick={handleCloseOverview}
									aria-label={GetLanguageValueAsString(translation, "Auto.Features.Task.Pages.TasksHome.L406C21.Close", "Close")}
									size="small"
									sx={{ position: "absolute", top: 12, right: 8 }}
								>
									<CloseIcon fontSize="small" />
								</IconButton>
							</DialogTitle>
							<DialogContent
								dividers
								sx={{
									p: 1,
									height: "80vh",
									display: "flex",
									flexDirection: "column",
									minHeight: 0,
								}}
							>
								<Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
									<TaskDashboard
										searchTerm={searchInput}
										onOpenTab={openTabFromOverview}
									/>
								</Box>
							</DialogContent>
						</Dialog>
					</Box>
				</TaskHeaderPrimaryActionContextProvider>
			</TaskHeaderActionsContextProvider>
		</TaskPageContextProvider>
	);
};
